import {
	Component,
	ElementRef,
	OnInit,
	Renderer2,
	ViewChild
} from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
// State
import { Store } from "@ngxs/store";
import { RulesState } from "../../../rules/state/rules.state";
import { QualityState } from '../../../state/quality.state';
// Model
import { ORDER_ACTIONS_MESSAGE, ORDER_STATE } from '../../../model/quality.constant';
import { OrderDetailModel } from "../../../state/model/order-detail.model";
import { APPROVE_ORDER_EVENT } from "../approve-modal/approve-modal.component";
import { OrdersRulesModel } from '../../../rules/model/ci-rule.model';
import { RULE_STATUS } from '../../../rules/model/constants';
// Modules
import { UPDATE_REJECTED_SUMMARY } from '@cv-invision/cv.invision-views.ux';
import { BroadcastEventModel, BroadcastService, ORDER_EVENT_TYPE, ToasterType } from "@cv-invision/core";
// Service
import { QualityDocumentService } from "../../../service/document-quality.service";
import { OverviewSubHeaderService } from "../service/overview-sub-header.service";

@Component({
	selector: 'cv-approve-reject-buttons',
	templateUrl: './approve-reject-buttons.component.html',
	styleUrls: ['./approve-reject-buttons.component.scss'],
})
export class ApproveRejectButtonsComponent implements OnInit {
	@ViewChild('rejectBoxBtn', { static: false }) rejectionBoxBtn: ElementRef<any>;
	@ViewChild('rejectBox', { static: false }) rejectionBox: ElementRef<any>;
	orderDetail$: Observable<OrderDetailModel> = this.store.select(QualityState.getOrderDetail);
	orderRules$: Observable<OrdersRulesModel> = this.store.select(RulesState.getOrderRules);
	isRecalled$: Observable<boolean> = this.store.select(QualityState.getIsbeingRecalled);

	orderDetail: OrderDetailModel;
	orderStatus = ORDER_STATE;
	orderRules: OrdersRulesModel;
	approveBtn: string;
	rejectBtn: string;
	isRecallingBtn: boolean;
	summary: string;
	rejectionBoxOpen: boolean;
	disabledReject = true;
	disabledApprove = true;

	get lockedBy(): any {
		return this.overviewSubheaderService.orderLocked;
	}

	private atComponentDestroy = new Subject();

	constructor(
		private qualityDocumentService: QualityDocumentService,
		private broadcastService: BroadcastService,
		private store: Store,
		private renderer: Renderer2,
		private overviewSubheaderService: OverviewSubHeaderService
	) {
		this.summary = '';
		this.rejectionBoxOpen = false;
		this.approveBtn = ORDER_STATE.APPROVE;
		this.rejectBtn = ORDER_STATE.REJECT;
		this.orderStatus = ORDER_STATE;
	}

	ngOnInit(): void {
		this.orderDetail$.pipe(takeUntil(this.atComponentDestroy)).subscribe((orderDetail: OrderDetailModel) => {
			const { stateEnum } = orderDetail;
			this.orderDetail = orderDetail;

			if ([ORDER_STATE.INQUALITY.toString(), ORDER_STATE.QUEUED.toString()].indexOf(stateEnum) > -1) {
				if (this.lockedBy.byTheUser) {
					this.disabledApprove = false;
					if (this.orderRules && this.orderRules.violation && this.orderRules.violation.length) {
						const rejectAppr = this.orderRules.violation.filter(v => v.status === RULE_STATUS.FAILED);
						if (this.lockedBy.byTheUser) {
							this.disabledReject = !rejectAppr.length;
						}
					}
				} else {
					this.disabledApprove = true;
					this.disabledReject = true;
				}
			}

			if (stateEnum === ORDER_STATE.REJECTED) {
				this.rejectBtn = ORDER_STATE.REJECTED;
			}
			if (stateEnum === ORDER_STATE.APPROVED) {
				this.approveBtn = ORDER_STATE.APPROVED;
			}

		});

		this.orderRules$.pipe(takeUntil(this.atComponentDestroy)).subscribe(oRules => {
			this.orderRules = oRules;
			if (oRules && oRules.violation && oRules.violation.length) {
				const rejectAppr = oRules.violation.filter(v => v.status === RULE_STATUS.FAILED);
				if (this.lockedBy.byTheUser) {
					this.disabledReject = !rejectAppr.length;
				}
			}
		});

		this.isRecalled$.pipe(takeUntil(this.atComponentDestroy)).subscribe(isC => {
			this.isRecallingBtn = isC;
			if (isC) {
				this.rejectBtn = ORDER_STATE.RECALLED;
			}
		});

		this.overviewSubheaderService
			.subheaderEvents
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((event: any) => {
				console.log(event);
				switch (event.name) {
					case ORDER_EVENT_TYPE.QRR_REPORT_GENERATED:
						break;
					case ORDER_EVENT_TYPE.ORDER_REJECTED:
						this.rejectBtn = ORDER_STATE.REJECTED;
						this.disabledReject = true;
						break;
					case ORDER_EVENT_TYPE.ORDER_APPROVED:
						this.approveBtn = ORDER_STATE.APPROVED;
						this.disabledApprove = true;
						break;
					case ORDER_EVENT_TYPE.ORDER_RESUBMITTED: {
						const { isValid } = event.data;
						if (isValid !== undefined && isValid === false) {
							this.isRecallingBtn = false;
							this.rejectBtn = ORDER_STATE.REJECTED;
							break;
						}
						this.rejectBtn = ORDER_STATE.REJECT;
						this.approveBtn = ORDER_STATE.APPROVE;
						this.disabledApprove = false;
						this.disabledReject = true;
						break;
					}
				}
			});

		this.broadcastService
			.onEvent(UPDATE_REJECTED_SUMMARY)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(async (broadcastEvent: BroadcastEventModel) => {
				if (this.rejectionBox.nativeElement.style.display === 'block') {
					this.qualityDocumentService
						.getOrderSummary(this.orderDetail.id)
						.subscribe(response => {
							this.summary = response.note;
						});
				}
			});
	}

	/**
	 * Approve the current order
	 */
	approveOrder(): void {
		if (this.approveValidations()) {
			this.openModalApprove();
		} else {
			this.disabledApprove = true;
			this.qualityDocumentService.approveOrder(this.orderDetail.id).subscribe(r => {
				if (r.isValid) {
					this.broadcastService.broadcast({
						name: ToasterType.SUCCESS,
						event: {
							message: ORDER_ACTIONS_MESSAGE.APPROVED
						}
					});
					this.overviewSubheaderService.subheaderEvents.next({ name: 'RETURN_HOME' })
				} else {
					this.disabledApprove = false;
				}
			});
		}
	}

	/**
	 * reject the current order
	 */
	rejectOrder(rejectionNote: string): void {
		if (!this.disabledReject) {
			this.disabledReject = true;
		}
		this.qualityDocumentService.rejectOrder(this.orderDetail.id, rejectionNote).subscribe(r => {
			if (r.isValid) {
				this.broadcastService.broadcast({
					name: ToasterType.SUCCESS,
					event: {
						message: ORDER_ACTIONS_MESSAGE.REJECTED
					}
				});
				this.overviewSubheaderService.subheaderEvents.next({ name: 'RETURN_HOME' })
			}
		});
	}

	/**
	 * open rejection box
	 */
	toggleRejectionBox(forceClose: boolean = false): void {
		if (forceClose || this.orderDetail.stateEnum !== this.orderStatus.REJECTED) {
			this.qualityDocumentService.getOrderSummary(this.orderDetail.id).subscribe(response => {
				this.summary = response.note;
			});

			this.rejectionBoxOpen = forceClose ? false : !this.rejectionBoxOpen;
			const { nativeElement } = this.rejectionBoxBtn;
			const { nativeElement: el } = this.rejectionBox;

			if (this.rejectionBoxOpen) {
				this.disabledApprove = true;
				const top = nativeElement.offsetTop + nativeElement.offsetHeight;
				this.renderer.setStyle(el, 'top', `${top + 2}px`);
				this.renderer.setStyle(el, 'display', 'block');
			} else {
				this.renderer.setStyle(el, 'display', 'none');
				this.disabledApprove = false;
			}
		}
	}

	/**
	 * validate if either approve or reject buttons can be enabled
	 * @param rulesCount
	 */
	private approveValidations(): boolean {
		const canSave = this.store.selectSnapshot(RulesState.getRulesCanSaveSate);
		const failedR = this.store
			.selectSnapshot(RulesState.getOrderRules)
			.violation.filter(v => v.status === RULE_STATUS.FAILED);
		const sv = canSave.filter(r => !r.canSave);

		if (sv.length || failedR.length) {
			return true;
		}
		return false;
	}

	/**
	 * open approve modal order
	 */
	openModalApprove(): void {
		this.broadcastService.broadcast({
			name: APPROVE_ORDER_EVENT,
			event: {
				data: this.orderDetail
			}
		});
	}
}
